import React, { useState, useMemo, useCallback, useEffect, useRef } from "react"

import logo from "@hydrologiq/shared/assets/hydrologiq_droplet.png"
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  IconButton,
  Input,
  Icon,
  Flex,
  Spinner,
  Image,
  MenuPositioner,
  DialogPositioner,
} from "@chakra-ui/react"
import { MenuRoot, MenuTrigger, MenuContent } from "@chakra-ui/react"
import {
  FaChevronLeft,
  FaChevronRight,
  FaCalendar,
  FaCheck,
  FaClock,
  FaFileInvoiceDollar,
  FaMagnifyingGlassMinus,
  FaMagnifyingGlassPlus,
  FaSpinner,
  FaWandMagicSparkles,
} from "react-icons/fa6"
import { FaInfoCircle, FaCog } from "react-icons/fa"
import { useMutationWithEndpoint, useQueryWithEndpoint, useLazyQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { DialogRoot, DialogContent, DialogHeader, DialogTitle, DialogBody } from "@chakra-ui/react"
import { Select } from "chakra-react-select"
import {
  format,
  addDays,
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
  parseISO,
  addWeeks,
  subWeeks,
} from "date-fns"
import { OrderWorkflowState } from "@hydrologiq/shared/contexts/OrderWorkflowContext"
import TimelineView, { ACTIVITY_KIND_CONFIGS } from "./TimelineView"
import SitesTimelineView from "./SitesTimelineView"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { useSuggestedFulfilments } from "./useSuggestedFulfilments"
import { useGetFeature } from "@hydrologiq/shared/providers/helpers/useFeature"
import { useDebounce } from "@hydrologiq/shared/hooks/useDebounce"
import { NativeSelectField, NativeSelectRoot } from "@hydrologiq/shared/chakra/NativeSelect"
import { FilterButton } from "@hydrologiq/shared/content/components/Button"
import { IANATimeZone } from "@hydrologiq/common/helpers/date"
import ActivityManage from "@hydrologiq/shared/components/activities/ActivityManage"

// Extract the Order type from GetOrdersQuery result
type OrderFromQuery = NonNullable<gqlTypes.GetOrdersQuery['orders']['data'][number]>

const GAP_VERT = 1
const GAP_HORIZ = 2

// Date Input Component - Isolated to prevent re-renders from closing date picker
interface DateInputProps {
  value: string
  onChange: (date: string) => void
  disabled?: boolean
}

const DateInput = React.memo<DateInputProps>(({ value, onChange, disabled }) => {
  const [datePending, setDatePending] = useState<string>(value)
  const debouncedDatePending = useDebounce<string>(datePending, 1500)
  const userIsEditingRef = useRef(false)

  useEffect(() => {
    if (debouncedDatePending && debouncedDatePending !== value) {
      onChange(debouncedDatePending)
    }
  }, [debouncedDatePending, onChange, value])

  // Update internal state when external value changes
  useEffect(() => {
    if (!userIsEditingRef.current) {
      setDatePending(value)
    }
  }, [value])

  return (
    <Input
      type="date"
      value={datePending}
      onChange={(e) => {
        const newDate = e.target.value
        userIsEditingRef.current = true
        setDatePending(newDate)
      }}
      onBlur={(e) => {
        userIsEditingRef.current = false
        // Immediately commit on blur as fallback
        if (e.target.value && e.target.value !== value) {
          onChange(e.target.value)
        }
      }}
      size="sm"
      width="160px"
      disabled={disabled}
    />
  )
})

// Type definitions - exported for use in fixtures and stories
export interface Location {
  id: string
  name: string
  lat: number
  long: number
}

export interface Customer {
  id: string
  name: string
}

export interface Site {
  id: string
  name: string
  location: Location | null
}

export interface Activity {
  id: string
  kind: gqlTypes.ActivityKind
  status: "pending" | "scheduled" | "started" | "completed"
  start: string
  end: string
  order_id?: string | null
  has_collection?: boolean
  has_delivery?: boolean
  order?: gqlTypes.AssetActivityOrder | null
}

export interface Asset {
  id: string
  name: string
  category: "hydrogen"
  class: "storage" | "power" | "monitoring" | "gas"
  kind: string
  status: string
  location: Location | null
  reserved_site?: Site | null
  image_url?: string | null
  brand_url?: string | null
  volume?: number | null
  min_pressure?: number | null
  created_at?: string
}

export interface AssetCalendarData {
  asset: Asset
  activities: Activity[]
}

export interface ApiResponse {
  data: AssetCalendarData[]
  meta: {
    page: number
    per_page: number
    total_assets: number
    total_pages: number
    date_range: {
      start: string
      end: string
      days: number
    }
    next_cursor?: string | null
    prev_cursor?: string | null
  }
}

interface AssetItem {
  id: string
  name: string
}

type Granularity = "quarter" | "month" | "week"

interface AssetAvailabilityCalendarProps<TState extends OrderWorkflowState = OrderWorkflowState> {
  data: ApiResponse
  booking?: boolean
  startDate: string
  endDate: string
  isRefreshing?: boolean
  onOrderDateChange?: (startDate: string, endDate: string, isEditing?: boolean) => void
  onAnchorDateChange?: (anchorDate: string) => void // Single date change for non-booking calendars
  onCalendarBufferChange?: (weeksBuffer: number) => void
  onAssetSelect?: (asset: Asset, orderStart: string, orderEnd: string) => void
  onAssetClick?: (asset: Asset) => void
  onOrderClick?: (order: gqlTypes.Order, asset: Asset) => void
  excludedAssetIds?: string[] // Assets that are already selected (to be marked as unavailable)
  disableStatusFilter?: boolean // If true, hide status filter controls and show all orders
  hasUnsavedChanges?: boolean // If true, show save/cancel buttons
  onSaveChanges?: () => void // Callback when user clicks save
  onDiscardChanges?: () => void // Callback when user clicks cancel/discard
  selectedAssetIds?: AssetItem[] // Local list of selected assets (before saving)
  onRemoveAsset?: (assetId: string) => void // Callback to remove an asset from local list
  onRefresh?: () => void // Callback to manually refresh the calendar data
  onLoadMore?: (cursor: string, direction: "forward" | "backward") => void // Callback to load more dates via cursor
  multipleAssets?: boolean // Whether to allow multiple assets to be selected
  highlightedAssetIds?: string[] // Assets to highlight from filtered order
  filteredOrderName?: string // Name of the filtered order
  onClearFilter?: () => void // Callback to clear order filter
  highlightedOrderDates?: { start: string; end: string } // Order date range to highlight
  filteredOrderId?: string // ID of the filtered order (for filtering AI activities)
  state?: TState
  onOrdersToggle?: () => void // Callback to toggle orders sidebar
  onActivityCreated?: () => void // Callback to refresh data after activity creation
  onActivityEdit?: (activityId: string, assetId: string) => void // Callback to open activity edit dialog
  granularity?: Granularity // Period granularity for non-booking mode (default: "month")
  onGranularityChange?: (granularity: Granularity) => void // Callback when granularity changes
  loadedDateRange?: { start: string; end: string } // Full loaded date range for infinite scroll (non-booking mode)
  onVisiblePeriodChange?: (periodStart: string) => void // Callback when visible period changes (non-booking mode)
  orders?: OrderFromQuery[] // Orders from getOrders query (for sites view)
}

// Helper functions for period calculations (defined before state to use in initialisers)
export const getPeriodStart = (date: Date, gran: "quarter" | "month" | "week", forceStart: boolean = false): Date => {
  switch (gran) {
    case "quarter":
      return startOfDay(date)
    case "month":
      return date.getDate() === 1 || forceStart ? startOfDay(startOfMonth(date)) : startOfDay(date)
    case "week":
      return startOfDay(date)
    default:
      return startOfDay(date)
  }
}

export const getPeriodEnd = (date: Date, gran: "quarter" | "month" | "week"): Date => {
  switch (gran) {
    case "quarter":
      return endOfDay(addDays(date, 89))
    case "month":
      return date.getDate() === 1 ? endOfDay(endOfMonth(date)) : endOfDay(addDays(date, 29))
    case "week":
      return endOfDay(addDays(date, 6))
    default:
      return endOfDay(date)
  }
}

export const addPeriod = (date: Date, granularity: Granularity, amount: number): Date => {
  switch (granularity) {
    case "quarter":
      // Add/subtract 3 months per period
      return amount >= 0 ? addMonths(date, amount * 3) : subMonths(date, Math.abs(amount) * 3)
    case "month":
      // Add/subtract months
      return amount >= 0 ? addMonths(date, amount) : subMonths(date, Math.abs(amount))
    case "week":
      // Add/subtract weeks
      return amount >= 0 ? addWeeks(date, amount) : subWeeks(date, Math.abs(amount))
    default:
      return date
  }
}

const AssetAvailabilityCalendarSimpleInner: React.FC<AssetAvailabilityCalendarProps> = ({
  data,
  booking = true,
  startDate,
  endDate,
  isRefreshing = false,
  onOrderDateChange,
  onAnchorDateChange,
  onCalendarBufferChange,
  onAssetSelect,
  onAssetClick,
  excludedAssetIds = [],
  disableStatusFilter = false,
  hasUnsavedChanges = false,
  onSaveChanges,
  onDiscardChanges,
  selectedAssetIds = [],
  onRemoveAsset,
  onRefresh,
  onLoadMore,
  highlightedAssetIds = [],
  filteredOrderName,
  filteredOrderId,
  onClearFilter,
  highlightedOrderDates,
  state,
  multipleAssets = false,
  onOrdersToggle,
  onActivityCreated,
  onActivityEdit,
  granularity: propGranularity,
  onGranularityChange,
  orders = [],
  loadedDateRange,
  onVisiblePeriodChange,
}) => {
  const calendarSitesEnabled = useGetFeature("calendar_sites")
  const bgColor = "white"

  const selectedAssetId = state?.selectedAssets?.[0]?.id

  // State for granularity (default to "month" for non-booking mode)
  const [granularity, setGranularity] = useState<Granularity>(propGranularity || "month")

  // Default view mode based on feature flag
  // In booking mode (activity editing), always default to "assets" view
  // which shows date-filtered availability
  const defaultViewMode = booking ? "assets" : calendarSitesEnabled ? "sites" : "assets"

  // Sync granularity with prop changes
  useEffect(() => {
    if (propGranularity) {
      setGranularity(propGranularity)
    }
  }, [propGranularity])

  // State for calendar buffer
  const [calendarBuffer, setCalendarBuffer] = useState<number>(4)

  // State for AI scheduling
  const planWithAIEnabled = !booking && useGetFeature("plan_with_ai")
  const [aiScheduleState, setAiScheduleState] = useState<"idle" | "running" | "completed">("idle")
  const [aiFulfilmentPlans, setAiFulfilmentPlans] = useState<gqlTypes.FulfilmentPlan[]>([])
  const [optimiseFulfilments] = useMutationWithEndpoint(gqlTypes.OptimiseFulfilmentsDocument, {}, "primary")

  // State for suggested fulfilment confirmation
  const [createActivityDialogOpen, setCreateActivityDialogOpen] = useState(false)
  const [activityPreFillData, setActivityPreFillData] = useState<{
    order: gqlTypes.Order | null
    availableAssets: gqlTypes.Asset[]
    startDate: string
    endDate: string
    quantity: number
    siteId: string
  } | null>(null)

  // Lazy queries for fetching data when confirming suggested fulfilment
  const [getAvailableAssets, { loading: loadingAvailableAssets }] = useLazyQueryWithEndpoint(
    gqlTypes.GetAvailableAssetsDocument,
    {},
    "primary"
  )

  // Local state for immediate date input values
  const [localStartDate, setLocalStartDate] = useState(startDate)
  const [localEndDate, setLocalEndDate] = useState(endDate)

  const startInputRef = useRef<HTMLInputElement>(null)
  const endInputRef = useRef<HTMLInputElement>(null)

  // Track if date change originated from user input (to prevent sync loops in booking mode)
  const userIsEditingRef = useRef(false)

  // Track if we're currently loading more data (to prevent duplicate requests during scroll)
  const isLoadingMoreRef = useRef(false)

  // Reset loading ref when refreshing completes (data loaded)
  useEffect(() => {
    if (!isRefreshing) {
      isLoadingMoreRef.current = false
    }
  }, [isRefreshing])

  // Track the last visible period start to detect when it changes
  const lastVisiblePeriodStartRef = useRef<string | null>(null)

  // Handle date commit (when user finishes editing)
  const commitDateChanges = useCallback(() => {
    if (!onOrderDateChange) return

    // Auto-correct if end date is before start date
    const start = new Date(localStartDate)
    const end = new Date(localEndDate)

    if (end < start) {
      // Set end date to one day after start date
      const correctedEndDate = format(addDays(start, 1), "yyyy-MM-dd")
      setLocalEndDate(correctedEndDate)
      // Pass isEditing=false to trigger refetch
      onOrderDateChange(localStartDate, correctedEndDate, false)
    } else if (localStartDate !== startDate || localEndDate !== endDate) {
      // Only call parent if dates actually changed
      // Pass isEditing=false to trigger refetch
      onOrderDateChange(localStartDate, localEndDate, false)
    }
  }, [localStartDate, localEndDate, startDate, endDate, onOrderDateChange])

  // Auto-commit date changes for non-booking mode only
  // Booking mode uses manual refresh button
  useEffect(() => {
    if (!booking && localStartDate && localEndDate) {
      commitDateChanges()
    }
  }, [localStartDate, localEndDate, booking, commitDateChanges])

  // Sync local dates with prop changes
  useEffect(() => {
    if (!booking) {
      // Non-booking mode: sync anchor date changes from navigation, but not during user input
      if (startDate && startDate !== localStartDate && !userIsEditingRef.current) {
        setLocalStartDate(startDate)
      }
      // Reset the flag after processing
      userIsEditingRef.current = false
    } else {
      // Booking mode: Only sync if change is external (not from user input)
      // This handles cases like "Discard Changes" where parent resets dates
      if (!userIsEditingRef.current) {
        if (startDate !== localStartDate) {
          setLocalStartDate(startDate)
        }
        if (endDate !== localEndDate) {
          setLocalEndDate(endDate)
        }
      }
      // Reset the flag after processing
      userIsEditingRef.current = false
    }
  }, [startDate, endDate, booking, localStartDate, localEndDate])

  // State for timeline controls
  const getDefaultTimeRange = useCallback(() => {
    // Non-booking mode: use granularity-based period
    if (!booking) {
      const anchorDate = startDate ? parseISO(startDate) : new Date()
      const periodStart = getPeriodStart(anchorDate, granularity)
      const periodEnd = getPeriodEnd(periodStart, granularity)
      return {
        start: periodStart.getTime(),
        end: periodEnd.getTime(),
      }
    }

    // Booking mode: Show 4 weeks maximum, starting 1 week before the start date
    const orderStart = new Date(startDate)
    const fourWeeksMs = 4 * 7 * 24 * 60 * 60 * 1000
    const oneWeekMs = 7 * 24 * 60 * 60 * 1000

    const start = startOfDay(new Date(orderStart.getTime() - oneWeekMs)).getTime()
    const end = start + fourWeeksMs

    return {
      start,
      end,
    }
  }, [startDate, booking, granularity])

  const defaultRange = getDefaultTimeRange()
  const [originalStartDate, _] = useState(defaultRange.start)
  const [visibleTimeStart, setVisibleTimeStart] = useState<number>(defaultRange.start)
  const [visibleTimeEnd, setVisibleTimeEnd] = useState<number>(defaultRange.end)
  const [filteredAssetIds, setfilteredAssetIds] = useState<string[]>([])
  const [userHasAdjustedView, setUserHasAdjustedView] = useState(false)
  const [viewMode, setViewMode] = useState<"assets" | "sites">(defaultViewMode)

  // Extract unique site IDs from calendar data and orders for fetching suggested fulfilments
  // Computed from full loaded data, not filtered by visible range, to avoid refetches on navigation
  const siteIds = useMemo(() => {
    if (viewMode !== "sites") return []

    const sites = new Set<string>()

    // From activities — all activities in loaded data, not filtered by visible range
    if (data?.data) {
      for (const assetData of data.data) {
        for (const activity of assetData.activities) {
          // Cast to any to access site property (it exists in the actual data but not in the type)
          const activityWithSite = activity as any
          if (activityWithSite.site?.id) {
            sites.add(activityWithSite.site.id)
          }
        }
      }
    }

    // From orders — ALL orders, not filtered by visible range
    for (const order of orders) {
      if (!order.sites) continue
      for (const orderSite of order.sites) {
        sites.add(orderSite.site.id)
      }
    }

    return Array.from(sites).sort()
  }, [viewMode, data, orders])

  console.log('[AssetAvailabilityCalendar] siteIds calculated:', {
    viewMode,
    siteIdsCount: siteIds.length,
    siteIds: siteIds.slice(0, 3),
    hasData: !!data,
    ordersCount: orders.length
  })

  // Fetch suggested fulfilments using custom hook
  const {
    suggestions: allSuggestedFulfilments,
    siteResponses,
    loading: isSuggestionsLoading,
    refetch: refetchSuggestedFulfilments
  } = useSuggestedFulfilments(
    siteIds,
    loadedDateRange ?? null,
    originalStartDate,
    viewMode === "sites"
  )

  // Create a stable key to force timeline re-render when suggestions change
  const suggestionsKey = useMemo(() => {
    return `suggestions-${allSuggestedFulfilments.length}-${siteResponses.size}`
  }, [allSuggestedFulfilments.length, siteResponses.size])

  // Only reset view if user hasn't manually adjusted it
  // This prevents resetting the view when buffer changes trigger refetch
  useEffect(() => {
    if (!userHasAdjustedView) {
      setVisibleTimeStart(defaultRange.start)
      setVisibleTimeEnd(defaultRange.end)
    }
  }, [startDate, endDate, userHasAdjustedView, defaultRange.start, defaultRange.end])

  // Update visible time range when order dates, granularity, or anchor date changes
  // This ensures the view updates when:
  // 1. User manually changes anchor date via input (non-booking mode)
  // 2. Anchor date changes after refetch completes (non-booking mode)
  // 3. Granularity changes (non-booking mode)
  // But NOT when buffer changes in booking mode (user is navigating)
  useEffect(() => {
    // Don't update during refresh to avoid flicker
    if (isRefreshing) return

    // Don't update if user has manually adjusted the view (e.g., via navigation buttons)
    // This prevents resetting the view when buffer changes trigger refetch in booking mode
    if (userHasAdjustedView) return

    const newRange = getDefaultTimeRange()

    // Only update if range actually changed (threshold of 1 second to avoid micro-adjustments)
    const startDiff = Math.abs(newRange.start - visibleTimeStart)
    const endDiff = Math.abs(newRange.end - visibleTimeEnd)
    if (startDiff > 1000 || endDiff > 1000) {
      setVisibleTimeStart(newRange.start)
      setVisibleTimeEnd(newRange.end)
    }
  }, [
    startDate,
    endDate,
    granularity,
    getDefaultTimeRange,
    isRefreshing,
    visibleTimeStart,
    visibleTimeEnd,
    userHasAdjustedView,
  ])

  // Order status filters - include completed and billed by default
  // When disabled, show all statuses
  const [activeOrderStatuses, setActiveOrderStatuses] = useState<Set<string>>(
    new Set(
      disableStatusFilter
        ? ["scheduled", "started", "completed", "billed"]
        : ["scheduled", "started", "completed", "billed"]
    )
  )

  // Activity type filter toggles (all active by default)
  const [activeActivityFilters, setActiveActivityFilters] = useState<Record<string, boolean>>(
    Object.fromEntries(Object.keys(ACTIVITY_KIND_CONFIGS).map((k) => [k, true]))
  )
  // Calculate which activity types are present in the visible time range
  const activityTypeCounts = useMemo(() => {
    if (!data || !data.data) return {}

    const counts: Record<string, number> = {
      fulfilment: 0,
      movement: 0,
      filling: 0,
      maintenance: 0,
      inspection: 0,
      hold: 0,
    }

    // Only count activities within the visible time range
    const visibleStart = visibleTimeStart
    const visibleEnd = visibleTimeEnd

    data.data.forEach((assetData) => {
      assetData.activities?.forEach((activity) => {
        if (counts[activity.kind] !== undefined) {
          const activityStart = new Date(activity.start).getTime()
          const activityEnd = new Date(activity.end).getTime()

          // Check if activity overlaps with visible range
          // Activity overlaps if it starts before visible end AND ends after visible start
          if (activityStart < visibleEnd && activityEnd > visibleStart) {
            counts[activity.kind]++
          }
        }
      })
    })

    return counts
  }, [data, visibleTimeStart, visibleTimeEnd])

  // Sync activeOrderStatuses when disableStatusFilter changes
  useEffect(() => {
    if (disableStatusFilter) {
      setActiveOrderStatuses(new Set(["scheduled", "started", "completed", "billed"]))
    }
  }, [disableStatusFilter])

  // Navigation handlers
  const handleZoomIn = useCallback(() => {
    if (!booking) return // Zoom only available in booking mode
    setUserHasAdjustedView(true)
    const currentRange = visibleTimeEnd - visibleTimeStart
    const newRange = currentRange * 0.75
    const minZoom = 24 * 60 * 60 * 1000 // 1 day

    // Don't zoom in more than 1 day
    if (newRange < minZoom) {
      return
    }

    const center = (visibleTimeStart + visibleTimeEnd) / 2
    setVisibleTimeStart(center - newRange / 2)
    setVisibleTimeEnd(center + newRange / 2)
  }, [visibleTimeStart, visibleTimeEnd, booking])

  const handleZoomOut = useCallback(() => {
    if (!booking) return // Zoom only available in booking mode
    setUserHasAdjustedView(true)
    const currentRange = visibleTimeEnd - visibleTimeStart
    const newRange = currentRange * 1.25
    const maxZoom = 31 * 24 * 60 * 60 * 1000 // 31 days

    // Don't zoom out more than 31 days
    if (newRange > maxZoom) {
      return
    }

    const center = (visibleTimeStart + visibleTimeEnd) / 2
    setVisibleTimeStart(center - newRange / 2)
    setVisibleTimeEnd(center + newRange / 2)
  }, [visibleTimeStart, visibleTimeEnd, booking])

  const handlePrevious = useCallback(() => {
    // Guard against null data during refetch
    if (!data?.meta?.date_range) return

    // Non-booking mode: navigate by granularity period
    if (!booking) {
      // Get current period start from visible range
      const currentPeriodStart = getPeriodStart(new Date(visibleTimeStart), granularity)

      // Calculate previous period boundaries
      const previousPeriodStart = getPeriodStart(addPeriod(currentPeriodStart, granularity, -1), granularity)
      const previousPeriodEnd = getPeriodEnd(previousPeriodStart, granularity)

      const newStart = previousPeriodStart.getTime()
      const newEnd = previousPeriodEnd.getTime()

      // Check if new period is within loaded data range
      const loadedStartTime = new Date(data.meta.date_range.start).getTime()
      const loadedEndTime = new Date(data.meta.date_range.end).getTime()
      const isWithinLoadedRange = newStart >= loadedStartTime && newEnd <= loadedEndTime

      if (isWithinLoadedRange) {
        // Within loaded range: update immediately (no refetch needed)
        // Update anchor date immediately since no refetch needed
        if (onAnchorDateChange) {
          onAnchorDateChange(format(previousPeriodStart, "yyyy-MM-dd"))
        }
      } else {
        // Outside loaded range: trigger refetch, update will happen after refetch completes
        if (onLoadMore && data.meta.prev_cursor) {
          // Store pending navigation to apply after refetch
          const pendingAnchorDate = format(previousPeriodStart, "yyyy-MM-dd")
          // Trigger load more - the parent component will handle updating anchor date after refetch
          onLoadMore(data.meta.prev_cursor, "backward")
          // Update anchor date - parent will handle syncing after data loads
          if (onAnchorDateChange) {
            onAnchorDateChange(pendingAnchorDate)
          }
        }
      }

      setVisibleTimeStart(newStart)
      setVisibleTimeEnd(newEnd)
      return
    }

    // Booking mode: original logic
    // Store current zoom level (visible range duration)
    const currentZoom = visibleTimeEnd - visibleTimeStart

    // Jump by 80% of current visible range (almost a full page)
    const moveAmount = currentZoom
    const newStart = visibleTimeStart - moveAmount
    const newEnd = newStart + currentZoom

    // Validate against data range bounds
    const minTime = new Date(data.meta.date_range.start).getTime()

    // Check if we need to load more data (approaching the boundary)
    if (newStart < minTime) {
      // Calculate how many weeks we need to extend the buffer
      const shortfall = minTime - newStart
      const weeksToAdd = Math.ceil(shortfall / (7 * 24 * 60 * 60 * 1000))

      // Increase calendar buffer to load more data (don't change order dates)
      if (onCalendarBufferChange) {
        // Add to existing buffer
        const currentBuffer = calendarBuffer || 4
        const newBuffer = currentBuffer + weeksToAdd
        onCalendarBufferChange(Math.min(newBuffer, 52)) // Cap at 52 weeks
      }

      // Clamp to current boundary for now (will adjust after data loads)
      setVisibleTimeStart(minTime)
      setVisibleTimeEnd(minTime + currentZoom)
    } else {
      setVisibleTimeStart(newStart)
      setVisibleTimeEnd(newEnd)
    }
  }, [
    visibleTimeStart,
    visibleTimeEnd,
    data?.meta?.date_range,
    data?.meta?.prev_cursor,
    calendarBuffer,
    onCalendarBufferChange,
    onLoadMore,
    booking,
    onAnchorDateChange,
    granularity,
  ])

  const handleNext = useCallback(() => {
    // Guard against null data during refetch
    if (!data?.meta?.date_range) return

    setUserHasAdjustedView(true)

    // Non-booking mode: navigate by granularity period
    if (!booking) {
      // Get current period start from visible range
      const currentPeriodStart = getPeriodStart(new Date(visibleTimeStart), granularity)

      // Calculate next period boundaries
      const nextPeriodStart = getPeriodStart(addPeriod(currentPeriodStart, granularity, 1), granularity)
      const nextPeriodEnd = getPeriodEnd(nextPeriodStart, granularity)

      const newStart = nextPeriodStart.getTime()
      const newEnd = nextPeriodEnd.getTime()

      // Check if new period is within loaded data range
      const loadedStartTime = new Date(data.meta.date_range.start).getTime()
      const loadedEndTime = new Date(data.meta.date_range.end).getTime()
      const isWithinLoadedRange = newStart >= loadedStartTime && newEnd <= loadedEndTime

      if (isWithinLoadedRange) {
        // Within loaded range: update immediately (no refetch needed)

        // Update anchor date immediately since no refetch needed
        if (onAnchorDateChange) {
          onAnchorDateChange(format(nextPeriodStart, "yyyy-MM-dd"))
        }
      } else {
        // Outside loaded range: trigger refetch, update will happen after refetch completes
        if (onLoadMore && data.meta.next_cursor) {
          // Store pending navigation to apply after refetch
          const pendingAnchorDate = format(nextPeriodStart, "yyyy-MM-dd")
          // Trigger load more - the parent component will handle updating anchor date after refetch
          onLoadMore(data.meta.next_cursor, "forward")
          // Update anchor date - parent will handle syncing after data loads
          if (onAnchorDateChange) {
            onAnchorDateChange(pendingAnchorDate)
          }
        }
      }
      setVisibleTimeStart(newStart)
      setVisibleTimeEnd(newEnd)
      return
    }

    // Booking mode: original logic
    // Store current zoom level (visible range duration)
    const currentZoom = visibleTimeEnd - visibleTimeStart

    // Jump by 80% of current visible range (almost a full page)
    const moveAmount = currentZoom
    const newStart = visibleTimeStart + moveAmount
    const newEnd = newStart + currentZoom

    // Validate against data range bounds
    const maxTime = new Date(data.meta.date_range.end).getTime()

    // Check if we need to load more data (approaching the boundary)
    if (newEnd > maxTime) {
      // Calculate how many weeks we need to extend the buffer
      const shortfall = newEnd - maxTime
      const weeksToAdd = Math.ceil(shortfall / (7 * 24 * 60 * 60 * 1000))

      // Increase calendar buffer to load more data (don't change order dates)
      if (onCalendarBufferChange) {
        // Add to existing buffer
        const currentBuffer = calendarBuffer || 4
        const newBuffer = currentBuffer + weeksToAdd
        onCalendarBufferChange(Math.min(newBuffer, 52)) // Cap at 52 weeks
      }

      // Clamp to current boundary for now (will adjust after data loads)
      setVisibleTimeEnd(maxTime)
      setVisibleTimeStart(maxTime - currentZoom)
    } else {
      setVisibleTimeStart(newStart)
      setVisibleTimeEnd(newEnd)
    }
  }, [
    visibleTimeStart,
    visibleTimeEnd,
    data?.meta?.date_range,
    data?.meta?.next_cursor,
    calendarBuffer,
    onCalendarBufferChange,
    onLoadMore,
    booking,
    onAnchorDateChange,
    granularity,
  ])

  const handleResetView = useCallback(() => {
    if (!data?.meta?.date_range) return

    // Reset userHasAdjustedView to allow view to update to the new anchor date
    setUserHasAdjustedView(false)

    // Set anchor date to today
    const now = new Date()

    const periodStart = getPeriodStart(now, granularity)

    onAnchorDateChange?.(format(periodStart, "yyyy-MM-dd"))
  }, [data?.meta?.date_range, granularity, onAnchorDateChange])

  // Handle AI scheduling
  const handleScheduleWithAI = useCallback(async () => {
    setAiScheduleState("running")
    try {
      // Get user's IANA timezone
      const userTimezone = IANATimeZone()

      const result = await optimiseFulfilments({
        variables: { timezone: userTimezone },
      })
      if (result.data) {
        setAiFulfilmentPlans(result.data.optimiseFulfilments.fulfilment_plans || [])
        setAiScheduleState("completed")
      } else {
        setAiScheduleState("idle")
      }
    } catch (error) {
      console.error("AI scheduling error:", error)
      alert("Failed to optimize schedules. Please try again.")
      setAiScheduleState("idle")
    }
  }, [optimiseFulfilments])

  const handleUndoAIScheduling = useCallback(() => {
    setAiFulfilmentPlans([])
    setAiScheduleState("idle")
  }, [])

  // Handle confirming a suggested fulfilment
  const handleConfirmSuggestedFulfilment = useCallback(
    async (suggestion: any) => {
      try {
        // Query available assets for the date range
        const assetsResult = await getAvailableAssets({
          variables: {
            start: suggestion.suggested_start_date,
            end: suggestion.suggested_end_date,
            per_page: 100,
          },
        })

        const availableAssets = assetsResult.data?.assets?.data || []

        if (availableAssets.length === 0) {
          alert("No available assets found for this date range")
          return
        }

        // Find the order from the calendar data
        // First try to find in activities (for backward compatibility)
        let orderObject: gqlTypes.Order | null = null
        for (const assetData of data.data) {
          for (const activity of assetData.activities) {
            if (activity.order_id === suggestion.order_id && activity.order) {
              orderObject = activity.order as any
              break
            }
          }
          if (orderObject) break
        }

        // If not found in activities, try the orders prop
        if (!orderObject) {
          const orderFromProp = orders.find((o: any) => o.id === suggestion.order_id)
          if (orderFromProp) {
            orderObject = orderFromProp as any
          }
        }

        if (!orderObject) {
          alert("Order not found in calendar data")
          return
        }

        // Set pre-fill data and open dialog
        setActivityPreFillData({
          order: orderObject,
          availableAssets: availableAssets as gqlTypes.Asset[],
          startDate: suggestion.suggested_start_date,
          endDate: suggestion.suggested_end_date,
          quantity: suggestion.suggested_quantity,
          siteId: suggestion.site_id,
        })
        setCreateActivityDialogOpen(true)
      } catch (error) {
        console.error("Error confirming suggested fulfilment:", error)
        alert("Failed to load data. Please try again.")
      }
    },
    [getAvailableAssets, data, orders]
  )

  // Handle calendar buffer changes
  const handleCalendarBufferChange = useCallback(
    (newBuffer: number) => {
      // Validate the buffer value (including NaN check)
      // Increased limit to 52 weeks (1 year) to support multiple 4-week navigation jumps
      if (isNaN(newBuffer) || newBuffer < 0 || newBuffer > 52) {
        alert("Buffer must be between 0 and 52 weeks")
        return
      }

      setCalendarBuffer(newBuffer)

      if (onCalendarBufferChange) {
        onCalendarBufferChange(newBuffer)
      }
    },
    [onCalendarBufferChange]
  )

  // Handle timeline time change with debouncing for performance
  const handleTimelineTimeChange = useCallback(
    (
      newVisibleTimeStart: number,
      newVisibleTimeEnd: number,
      updateScrollCanvas: (start: number, end: number) => void
    ) => {
      // Guard against null data during refetch
      if (!data?.meta?.date_range) return

      const minTime = new Date(data.meta.date_range.start).getTime()
      const maxTime = new Date(data.meta.date_range.end).getTime()

      // Enforce zoom limits
      const minZoom = 24 * 60 * 60 * 1000 // 1 day
      const maxZoom = 31 * 24 * 60 * 60 * 1000 // 31 days
      const currentZoom = newVisibleTimeEnd - newVisibleTimeStart

      let constrainedStart = newVisibleTimeStart
      let constrainedEnd = newVisibleTimeEnd

      // If zoom is too far out, constrain it
      if (currentZoom > maxZoom) {
        const center = (newVisibleTimeStart + newVisibleTimeEnd) / 2
        constrainedStart = center - maxZoom / 2
        constrainedEnd = center + maxZoom / 2
      }

      // If zoom is too far in, constrain it
      if (currentZoom < minZoom) {
        const center = (newVisibleTimeStart + newVisibleTimeEnd) / 2
        constrainedStart = center - minZoom / 2
        constrainedEnd = center + minZoom / 2
      }

      // Non-booking mode: allow infinite scrolling (no boundary constraints)
      // Booking mode: enforce strict boundaries to prevent panning beyond data range
      if (booking) {
        // If trying to pan before start, clamp to start
        if (constrainedStart < minTime) {
          const zoom = constrainedEnd - constrainedStart
          constrainedStart = minTime
          constrainedEnd = minTime + zoom
        }

        // If trying to pan after end, clamp to end
        if (constrainedEnd > maxTime) {
          const zoom = constrainedEnd - constrainedStart
          constrainedEnd = maxTime
          constrainedStart = maxTime - zoom
        }

        // Final safety check - ensure we're still within bounds after adjustments
        constrainedStart = Math.max(constrainedStart, minTime)
        constrainedEnd = Math.min(constrainedEnd, maxTime)
      }

      // Non-booking mode: infinite scroll with dynamic data fetching
      if (!booking && loadedDateRange && onLoadMore && !isLoadingMoreRef.current && !isRefreshing) {
        const loadedStart = new Date(loadedDateRange.start).getTime()
        const loadedEnd = new Date(loadedDateRange.end).getTime()
        const visibleRange = constrainedEnd - constrainedStart

        // Fetch threshold: trigger when within 20% of visible range from the edge
        const fetchThreshold = visibleRange * 0.2

        // Check if approaching the start of loaded data (scrolling backward)
        if (constrainedStart < loadedStart + fetchThreshold && data.meta.prev_cursor) {
          isLoadingMoreRef.current = true
          onLoadMore(data.meta.prev_cursor, "backward")
          // Reset loading flag after a short delay to allow next request
          setTimeout(() => {
            isLoadingMoreRef.current = false
          }, 1000)
        }

        // Check if approaching the end of loaded data (scrolling forward)
        if (constrainedEnd > loadedEnd - fetchThreshold && data.meta.next_cursor) {
          isLoadingMoreRef.current = true
          onLoadMore(data.meta.next_cursor, "forward")
          // Reset loading flag after a short delay to allow next request
          setTimeout(() => {
            isLoadingMoreRef.current = false
          }, 1000)
        }
      }

      // Only update if values actually changed
      if (Math.abs(constrainedStart - visibleTimeStart) > 100 || Math.abs(constrainedEnd - visibleTimeEnd) > 100) {
        setVisibleTimeStart(constrainedStart)
        setVisibleTimeEnd(constrainedEnd)

        // Mark that user has scrolled to prevent useEffect from resetting the view
        if (!booking) {
          setUserHasAdjustedView(true)
        }

        // Non-booking mode: update date input to reflect the leftmost visible date
        if (!booking && onVisiblePeriodChange) {
          // Use the start of the visible range (leftmost date)
          const visibleStartDate = startOfDay(new Date(constrainedStart))
          const visibleStartDateStr = format(visibleStartDate, "yyyy-MM-dd")

          // Only trigger callback if the date actually changed
          if (visibleStartDateStr !== lastVisiblePeriodStartRef.current) {
            lastVisiblePeriodStartRef.current = visibleStartDateStr
            onVisiblePeriodChange(visibleStartDateStr)
          }
        }
      }

      // Always update scroll canvas with constrained values to prevent visual glitches (booking mode only)
      if (booking && (constrainedStart !== newVisibleTimeStart || constrainedEnd !== newVisibleTimeEnd)) {
        updateScrollCanvas(constrainedStart, constrainedEnd)
      }
    },
    [
      data?.meta?.date_range,
      data?.meta?.prev_cursor,
      data?.meta?.next_cursor,
      visibleTimeStart,
      visibleTimeEnd,
      booking,
      loadedDateRange,
      onLoadMore,
      isRefreshing,
      onVisiblePeriodChange,
      granularity,
    ]
  )

  // Handle asset selection
  const handleAssetSelect = useCallback(
    (asset: Asset, orderStart: string, orderEnd: string) => {
      if (onAssetSelect) {
        // Check if asset is already selected if so unselect
        if (selectedAssetIds.some((a) => a.id === asset.id)) {
          onRemoveAsset?.(asset.id)
        } else {
          onAssetSelect(asset, orderStart, orderEnd)
        }
      } else if (onAssetClick) {
        onAssetClick(asset)
      }
    },
    [onAssetSelect, onAssetClick, selectedAssetIds, onRemoveAsset]
  )

  // Helper function to check if asset is available for the order date range
  const isAssetAvailable = useCallback(
    (assetData: AssetCalendarData) => {
      const orderStartTime = startOfDay(new Date(startDate)).getTime()
      const orderEndTime = endOfDay(new Date(endDate)).getTime()

      // Check for overlapping activities
      const hasActivityConflict = assetData.activities.some((activity) => {
        const activityStart = new Date(activity.start).getTime()
        const activityEnd = new Date(activity.end).getTime()

        // Check for overlap: activity overlaps if it starts before order ends AND ends after order starts
        return activityStart <= orderEndTime && activityEnd >= orderStartTime
      })

      return !hasActivityConflict
    },
    [startDate, endDate]
  )

  // Get all assets for multi-select
  const allAssets = useMemo(() => {
    return data.data.map((d) => ({
      id: d.asset.id,
      name: d.asset.name,
      status: d.asset.status,
      kind: d.asset.kind,
    }))
  }, [data])

  // Calculate filtered and available assets count
  const { filteredAssetsCount, availableAssetsCount } = useMemo(() => {
    const filtered = data.data.filter((d) => filteredAssetIds.length === 0 || filteredAssetIds.includes(d.asset.id))
    const available = filtered.filter((d) => isAssetAvailable(d))

    return {
      filteredAssetsCount: filtered.length,
      availableAssetsCount: available.length,
    }
  }, [data.data, filteredAssetIds, isAssetAvailable])

  // Memoize selected asset IDs array to prevent creating new reference on every render
  const selectedAssetIdsArray = useMemo(() => {
    return selectedAssetIds?.map((a) => a.id) || []
  }, [selectedAssetIds])

  // Calculate assets with AI activities for the filtered order and combine with existing highlighted assets
  const combinedHighlightedAssetIds = useMemo(() => {
    // Start with existing highlighted assets (from order.assets)
    const baseAssetIds = new Set(highlightedAssetIds)

    // If we have a filtered order ID and AI plans, add assets that have AI plans for that order
    if (filteredOrderId && aiFulfilmentPlans.length > 0) {
      const assetsWithAIPlans = aiFulfilmentPlans
        .filter((plan) => plan.order.id === filteredOrderId)
        .map((plan) => plan.asset.id)

      assetsWithAIPlans.forEach((assetId) => baseAssetIds.add(assetId))
    }

    return Array.from(baseAssetIds)
  }, [highlightedAssetIds, filteredOrderId, aiFulfilmentPlans])

  const handleGranularityChange = useCallback(
    (newGranularity: Granularity) => {
      setGranularity(newGranularity)
      onGranularityChange?.(newGranularity)

      // Calculate new period based on current anchor date
      const anchor = startDate ? parseISO(startDate) : new Date()
      const periodStart = getPeriodStart(anchor, newGranularity)
      const periodEnd = getPeriodEnd(periodStart, newGranularity)

      const newStart = periodStart.getTime()
      const newEnd = periodEnd.getTime()

      // Check if new period is within loaded data range
      if (data?.meta?.date_range) {
        const loadedStartTime = new Date(data.meta.date_range.start).getTime()
        const loadedEndTime = new Date(data.meta.date_range.end).getTime()
        const isWithinLoadedRange = newStart >= loadedStartTime && newEnd <= loadedEndTime

        if (isWithinLoadedRange) {
          // Within loaded range: update view immediately (no refetch needed)
          setVisibleTimeStart(newStart)
          setVisibleTimeEnd(newEnd)
          setUserHasAdjustedView(false)
        } else {
          // Outside loaded range: update view but don't trigger refetch here
          // The parent component should handle refetch if needed
          setVisibleTimeStart(newStart)
          setVisibleTimeEnd(newEnd)
          setUserHasAdjustedView(false)
        }
      } else {
        // No data yet: just update view
        setVisibleTimeStart(newStart)
        setVisibleTimeEnd(newEnd)
        setUserHasAdjustedView(false)
      }
    },
    [startDate, onGranularityChange, data?.meta?.date_range]
  )

  const DateSelection = () => {
    // Non-booking mode: single anchor date field with granularity selector
    if (!booking) {
      return (
        <HStack gap={2} align="center">
          <DateInput
            value={localStartDate}
            onChange={(date) => {
              setLocalStartDate(date)
              if (onAnchorDateChange) {
                onAnchorDateChange(date)
              }
            }}
            disabled={isRefreshing}
          />
          <NativeSelectRoot disabled={isRefreshing} size="sm" width="110px">
            <NativeSelectField
              value={granularity}
              onChange={(e) => handleGranularityChange(e.target.value as Granularity)}
              items={[
                { label: "Month", value: "month" },
                { label: "Week", value: "week" },
              ]}
            />
          </NativeSelectRoot>
        </HStack>
      )
    }

    // Booking mode: start and end date fields
    return (
      <HStack gap={2} align="center">
        <Input
          type="datetime-local"
          value={localStartDate}
          onChange={(e) => {
            const newValue = e.target.value
            userIsEditingRef.current = true
            setLocalStartDate(newValue)
            if (onOrderDateChange) {
              onOrderDateChange(newValue, localEndDate, true)
            }
          }}
          ref={startInputRef}
          size="sm"
          width="175px"
          disabled={isRefreshing}
        />
        <Text fontSize="sm" color="gray.500">→</Text>
        <Input
          type="datetime-local"
          value={localEndDate}
          onChange={(e) => {
            const newValue = e.target.value
            userIsEditingRef.current = true
            setLocalEndDate(newValue)
            if (onOrderDateChange) {
              onOrderDateChange(localStartDate, newValue, true)
            }
          }}
          ref={endInputRef}
          size="sm"
          width="175px"
          disabled={isRefreshing}
        />
      </HStack>
    )
  }

  return (
    <VStack gap={GAP_VERT} width="100%" height="100%" align="stretch">
      {/* Header Controls */}
      <Box py={0} pb={2}>
        <VStack gap={2} align="stretch">
          <HStack
            justifyContent="space-between"
            align={{ base: "start", lg: "center" }}
            width="100%"
            flexDirection={{ base: "column", lg: "row" }}
            gap={2}
          >
            <HStack align={"center"} height="100%" flexWrap="wrap" gap={2}>
              {/* Navigation Controls */}

              <HStack gap={1}>
                {!booking && (
                  <Button onClick={handleResetView} variant={"hydrologiq_secondary" as any} size="sm">
                    Today
                  </Button>
                )}
                <IconButton
                  aria-label="Previous period"
                  onClick={handlePrevious}
                  variant={"hydrologiq_secondary" as any}
                  size="sm"
                  disabled={isRefreshing || !data?.meta?.date_range}
                >
                  <FaChevronLeft />
                </IconButton>
                {booking && (
                  <>
                    <IconButton
                      onClick={handleZoomOut}
                      variant={"hydrologiq_secondary" as any}
                      size="sm"
                      disabled={isRefreshing || !data?.meta?.date_range}
                    >
                      <FaMagnifyingGlassMinus />
                    </IconButton>
                    <IconButton
                      onClick={handleZoomIn}
                      variant={"hydrologiq_secondary" as any}
                      size="sm"
                      disabled={isRefreshing || !data?.meta?.date_range}
                    >
                      <FaMagnifyingGlassPlus />
                    </IconButton>
                  </>
                )}
                <IconButton
                  aria-label="Next period"
                  onClick={handleNext}
                  variant={"hydrologiq_secondary" as any}
                  size="sm"
                  disabled={isRefreshing || !data?.meta?.date_range}
                >
                  <FaChevronRight />
                </IconButton>
              </HStack>
              <DateSelection />
              {booking && (
                <MenuRoot>
                  <MenuTrigger>
                    <IconButton aria-label="Calendar settings" size="sm" variant="outline">
                      <FaCog />
                    </IconButton>
                  </MenuTrigger>
                  <MenuPositioner>
                    <MenuContent>
                      <Box p={3}>
                        <VStack align="stretch" gap={GAP_VERT}>
                          <Text fontSize="sm" fontWeight="semibold">
                            Showing additional weeks before/after
                          </Text>
                          <HStack gap={GAP_HORIZ}>
                            <Input
                              type="number"
                              value={calendarBuffer}
                              onChange={(e) => {
                                const val = parseInt(e.target.value)
                                if (!isNaN(val) && val >= 0 && val <= 52) {
                                  handleCalendarBufferChange(val)
                                }
                              }}
                              onBlur={(e) => {
                                const val = parseInt(e.target.value)
                                if (isNaN(val) || val < 0 || val > 52) {
                                  e.target.value = calendarBuffer.toString()
                                }
                              }}
                              size="sm"
                              width="60px"
                              textAlign="center"
                              min={0}
                              max={52}
                            />
                          </HStack>
                          <Text fontSize="xs" color="gray.500">
                            Currently: ±{calendarBuffer} week{calendarBuffer !== 1 ? "s" : ""}
                          </Text>
                        </VStack>
                      </Box>
                    </MenuContent>
                  </MenuPositioner>
                </MenuRoot>
              )}
            </HStack>
            {/* Actions */}
            <HStack gap={2} align={"center"}>
              {planWithAIEnabled && (
                <>
                  {aiScheduleState === "idle" && (
                    <Button onClick={handleScheduleWithAI} variant={"magic" as any} size="sm">
                      <FaWandMagicSparkles style={{ marginRight: "6px" }} />
                      Plan with AI
                    </Button>
                  )}
                  {aiScheduleState === "completed" && (
                    <Button onClick={handleUndoAIScheduling} variant={"hydrologiq_primary" as any} size="sm">
                      Undo AI Plan
                    </Button>
                  )}
                </>
              )}
              {!booking ? !calendarSitesEnabled ? onOrdersToggle ? <FilterButton
                context={{ label: "Orders", description: "Filter orders", variant: "secondary" }}
                onClick={() => onOrdersToggle()}
              /> : null : <Button onClick={() => setViewMode(viewMode === "assets" ? "sites" : "assets")} variant={"hydrologiq_secondary" as any} size="sm">
                Switch to {viewMode === "assets" ? "sites" : "assets"} view
              </Button> : null}
            </HStack>
          </HStack>

          {/* Filters Row */}
          <HStack
            gap={GAP_HORIZ}
            width="100%"
            justify="space-between"
            align={{ base: "start", lg: "end" }}
            flexDirection={{ base: "column", lg: "row" }}
            display={booking ? undefined : "none"}
          >
            {/* Clear filter banner */}
            {filteredOrderName && onClearFilter && (
              <HStack gap={GAP_HORIZ} wrap="wrap" bg="blue.50" p={3} borderRadius="md">
                <HStack justify="space-between" align="center" flex={1}>
                  <HStack gap={GAP_HORIZ} align="center">
                    <Icon color="blue.500">
                      <FaInfoCircle />
                    </Icon>
                    <Text fontSize="sm" fontWeight="medium">
                      Filtering by order: <strong>{filteredOrderName}</strong>
                    </Text>
                  </HStack>
                  <Button size="sm" variant={"hydrologiq_secondary" as any} onClick={onClearFilter}>
                    Clear Filter
                  </Button>
                </HStack>
              </HStack>
            )}

            {!disableStatusFilter && (
              <HStack gap={GAP_HORIZ} wrap="wrap">
                {/* Settings Menu */}

                <Text fontSize="sm" fontWeight="semibold">
                  Order Status:
                </Text>
                <Box
                  as="button"
                  px={3}
                  py={1}
                  borderRadius="md"
                  cursor="pointer"
                  bg={activeOrderStatuses.has("scheduled") ? "#FEEBC8" : "gray.100"}
                  color={"primary"}
                  opacity={activeOrderStatuses.has("scheduled") ? 1 : 0.6}
                  transition="all 0.2s"
                  _hover={{
                    opacity: 1,
                    bg: activeOrderStatuses.has("scheduled") ? "#FED7AA" : "gray.200",
                  }}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setActiveOrderStatuses((prev) => {
                      const next = new Set(prev)
                      if (next.has("scheduled")) {
                        next.delete("scheduled")
                      } else {
                        next.add("scheduled")
                      }
                      return next
                    })
                  }}
                >
                  <HStack gap={GAP_HORIZ}>
                    <FaClock style={{ fontSize: "12px" }} />
                    <Text fontSize="sm" fontWeight="medium">
                      Scheduled
                    </Text>
                  </HStack>
                </Box>
                <Box
                  as="button"
                  px={3}
                  py={1}
                  borderRadius="md"
                  cursor="pointer"
                  bg={activeOrderStatuses.has("started") ? "#BEE3F8" : "gray.100"}
                  color={activeOrderStatuses.has("started") ? "#1e3a8a" : "gray.500"}
                  opacity={activeOrderStatuses.has("started") ? 1 : 0.6}
                  transition="all 0.2s"
                  _hover={{
                    opacity: 1,
                    bg: activeOrderStatuses.has("started") ? "#90CDF4" : "gray.200",
                  }}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setActiveOrderStatuses((prev) => {
                      const next = new Set(prev)
                      if (next.has("started")) {
                        next.delete("started")
                      } else {
                        next.add("started")
                      }
                      return next
                    })
                  }}
                >
                  <HStack gap={GAP_HORIZ}>
                    <FaSpinner style={{ fontSize: "12px" }} />
                    <Text fontSize="sm" fontWeight="medium">
                      In Progress
                    </Text>
                  </HStack>
                </Box>
                <Box
                  as="button"
                  px={3}
                  py={1}
                  borderRadius="md"
                  cursor="pointer"
                  bg={activeOrderStatuses.has("completed") ? "#C6F6D5" : "gray.100"}
                  color={activeOrderStatuses.has("completed") ? "#14532d" : "gray.500"}
                  opacity={activeOrderStatuses.has("completed") ? 1 : 0.6}
                  transition="all 0.2s"
                  _hover={{
                    opacity: 1,
                    bg: activeOrderStatuses.has("completed") ? "#9AE6B4" : "gray.200",
                  }}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setActiveOrderStatuses((prev) => {
                      const next = new Set(prev)
                      if (next.has("completed")) {
                        next.delete("completed")
                      } else {
                        next.add("completed")
                      }
                      return next
                    })
                  }}
                >
                  <HStack gap={GAP_HORIZ}>
                    <FaCheck style={{ fontSize: "12px" }} />
                    <Text fontSize="sm" fontWeight="medium">
                      Completed
                    </Text>
                  </HStack>
                </Box>
                <Box
                  as="button"
                  px={3}
                  py={1}
                  borderRadius="md"
                  cursor="pointer"
                  bg={activeOrderStatuses.has("billed") ? "#E9D8FD" : "gray.100"}
                  color={"primary"}
                  opacity={activeOrderStatuses.has("billed") ? 1 : 0.6}
                  transition="all 0.2s"
                  _hover={{
                    opacity: 1,
                    bg: activeOrderStatuses.has("billed") ? "#D6BCFA" : "gray.200",
                  }}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setActiveOrderStatuses((prev) => {
                      const next = new Set(prev)
                      if (next.has("billed")) {
                        next.delete("billed")
                      } else {
                        next.add("billed")
                      }
                      return next
                    })
                  }}
                >
                  <HStack gap={GAP_HORIZ}>
                    <FaFileInvoiceDollar style={{ fontSize: "12px" }} />
                    <Text fontSize="sm" fontWeight="medium">
                      Billed
                    </Text>
                  </HStack>
                </Box>
              </HStack>
            )}
          </HStack>

          {/* Asset Multi-Select */}
          <HStack
            justify="space-between"
            gap={GAP_HORIZ}
            flexDirection={{ base: "column", lg: "row" }}
            alignItems="end"
          >
            <HStack gap={GAP_HORIZ} align={"end"}>
              {hasUnsavedChanges && (
                <HStack gap={GAP_HORIZ} wrap="wrap">
                  <HStack justify="space-between" align="center">
                    <HStack gap={GAP_HORIZ} align="center">
                      <Icon>
                        <FaInfoCircle />
                      </Icon>
                      <Text fontSize="sm" fontWeight="medium">
                        You have unsaved changes
                      </Text>
                    </HStack>
                    <HStack gap={GAP_HORIZ}>
                      <Button variant={"hydrologiq_secondary" as any} onClick={onDiscardChanges}>
                        Reset
                      </Button>
                      <Button variant={"hydrologiq_primary" as any} onClick={onSaveChanges}>
                        Save Changes
                      </Button>
                    </HStack>
                  </HStack>
                </HStack>
              )}
            </HStack>
          </HStack>

          {/* Activity Filter Pills */}
          <HStack
            width="100%"
            justify="space-between"
            align={{ base: "start", lg: "center" }}
            flexDirection={{ base: "column", lg: "row" }}
            gap={GAP_HORIZ}
          >
            <HStack gap={2} wrap="wrap">
              {booking && data?.meta?.date_range && (
                <Text fontSize="sm" color="fg.muted" mr={1}>
                  Showing <b>{filteredAssetsCount} assets</b>, <b>{availableAssetsCount} available</b>
                </Text>
              )}
              {(Object.keys(ACTIVITY_KIND_CONFIGS) as Array<keyof typeof ACTIVITY_KIND_CONFIGS>).map((kind) => {
                const config = ACTIVITY_KIND_CONFIGS[kind]
                const count = activityTypeCounts[kind] || 0
                const isActive = activeActivityFilters[kind]

                // In sites view, hide pills with zero count
                if (viewMode === "sites" && count === 0) return null

                // In sites view, pills are display-only (no toggle)
                const isSitesView = viewMode === "sites"

                return (
                  <Box
                    key={kind}
                    as="button"
                    onClick={isSitesView ? undefined : () => setActiveActivityFilters((prev) => ({ ...prev, [kind]: !prev[kind] }))}
                    px={3}
                    py={1}
                    borderRadius="full"
                    cursor={isSitesView ? "default" : "pointer"}
                    display="inline-flex"
                    alignItems="center"
                    gap={2}
                    fontSize="xs"
                    fontWeight="semibold"
                    transition="all 0.15s"
                    bg={isActive ? config.backgroundColor : "white"}
                    color={isActive ? config.textColor : "gray.500"}
                    border="1.5px solid"
                    borderColor={isActive ? config.backgroundColor : "gray.200"}
                    opacity={isActive ? 1 : 0.7}
                    _hover={isSitesView ? undefined : { opacity: 1, borderColor: isActive ? config.textColor : "gray.400" }}
                  >
                    <config.icon style={{ fontSize: "11px" }} />
                    <span>{kind.charAt(0).toUpperCase() + kind.slice(1)}</span>
                    <Box
                      as="span"
                      minW="18px"
                      h="18px"
                      px="5px"
                      borderRadius="full"
                      fontSize="10px"
                      fontWeight="bold"
                      display="inline-flex"
                      alignItems="center"
                      justifyContent="center"
                      bg={isActive ? "rgba(255,255,255,0.65)" : "gray.100"}
                      color={isActive ? config.textColor : "gray.500"}
                    >
                      {count}
                    </Box>
                  </Box>
                )
              })}
            </HStack>

            {/* Asset filter / loading indicator */}
            <HStack position="relative" minW={{ base: "100%", lg: "400px" }} maxW={{ base: "100%", lg: "400px" }}>
              {viewMode === "assets" && !isRefreshing && !isSuggestionsLoading && (
                <Select
                  isMulti
                  name="assets"
                  options={allAssets.map((asset) => ({
                    value: asset.id,
                    label: asset.name,
                  }))}
                  value={allAssets
                    .filter((asset) => filteredAssetIds.includes(asset.id))
                    .map((asset) => ({
                      value: asset.id,
                      label: asset.name,
                    }))}
                  onChange={(selected) => {
                    setfilteredAssetIds(selected ? selected.map((s) => s.value) : [])
                  }}
                  placeholder="Filter assets..."
                  size="sm"
                  chakraStyles={{
                    container: (provided) => ({
                      ...provided,
                      width: "100%",
                      zIndex: 1000,
                      bg: "white !important"
                    }),
                    placeholder: (provided) => ({
                      ...provided,
                      pl: "5px"
                    })
                  }}
                />
              )}
            </HStack>
          </HStack>
        </VStack>
      </Box>

      {/* Timeline */}
      <Box p={0} bg={bgColor} borderRadius="0" flex={1} display="flex" flexDirection="column" overflowX="hidden">
        <Box height="100%" width="100%" position="relative" minHeight="600px">
          {/* Loading overlay — sits on top of the timeline during refetch/suggestions loading */}
          {(isRefreshing || isSuggestionsLoading) && (
            <Flex
              position="absolute"
              inset={0}
              zIndex={20}
              bg="rgba(255,255,255,0.6)"
              backdropFilter="blur(1px)"
              align="center"
              justify="center"
              direction="column"
              gap={3}
              borderRadius="md"
              pointerEvents="none"
            >
              <Spinner size="lg" color="blue.500" borderWidth="3px" />
              <Text fontSize="sm" fontWeight="semibold" color="fg.muted">
                {isRefreshing ? "Refreshing calendar data..." : "Fetching delivery suggestions..."}
              </Text>
            </Flex>
          )}

          {data.data.length > 0 ? (
            viewMode === "sites" ? (
              <SitesTimelineView
                key={suggestionsKey}
                data={data as any}
                visibleTimeStart={visibleTimeStart}
                visibleTimeEnd={visibleTimeEnd}
                filteredAssetIds={filteredAssetIds}
                suggestedFulfilments={allSuggestedFulfilments}
                siteResponses={siteResponses}
                orders={orders}
                onTimeChange={handleTimelineTimeChange}
                onActivityEdit={onActivityEdit}
                onSwitchToAssetsView={() => setViewMode("assets")}
                onConfirmSuggestedFulfilment={handleConfirmSuggestedFulfilment}
              />
            ) : (
              <TimelineView
                data={data as any}
                booking={booking}
                orderStartDate={startDate}
                orderEndDate={endDate}
                visibleTimeStart={visibleTimeStart}
                visibleTimeEnd={visibleTimeEnd}
                filteredAssetIds={filteredAssetIds}
                activeOrderStatuses={activeOrderStatuses}
                selectedAssetId={selectedAssetId}
                excludedAssetIds={excludedAssetIds}
                isRefreshing={isRefreshing}
                onTimeChange={handleTimelineTimeChange}
                onAssetSelect={handleAssetSelect}
                onAssetUnselect={onRemoveAsset}
                onItemSelect={() => { }}
                selectedAssetIds={selectedAssetIdsArray}
                multipleAssets={multipleAssets}
                onActivityCreated={onActivityCreated}
                onActivityEdit={onActivityEdit}
                highlightedAssetIds={combinedHighlightedAssetIds}
                highlightedOrderDates={highlightedOrderDates}
                aiFulfilmentPlans={aiFulfilmentPlans}
                activeActivityKinds={activeActivityFilters}
              />
            )
          ) : (
            <Flex height="400px" align="center" justify="center" direction="column" gap={GAP_HORIZ}>
              <Icon boxSize={12} color="fg.subtle">
                <FaCalendar />
              </Icon>
              <Text color="gray.500" fontSize="lg">
                No assets match your current filters
              </Text>
              <Button
                size="sm"
                onClick={() => {
                  setfilteredAssetIds([])
                }}
              >
                Clear Filters
              </Button>
            </Flex>
          )}
        </Box>
      </Box>

      {/* AI Scheduling Loading Dialog */}
      <DialogRoot open={aiScheduleState === "running"} closeOnEscape={true} closeOnInteractOutside={false}>
        <DialogPositioner>
          <DialogContent background={"highlight"} color="white" fontSize="md">
            <DialogHeader>
              <DialogTitle>AI Planning in progress...</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <VStack gap={GAP_HORIZ}>
                <Box
                  position="relative"
                  width="90px"
                  height="90px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Box
                    width="90px"
                    height="90px"
                    borderRadius="50%"
                    border="3px solid"
                    borderColor="transparent"
                    background={`conic-gradient(from 0deg at 50% 50%, #39aed2 0%, #8f2eff 100%)`}
                    animation="spin 1s linear infinite"
                    css={{
                      mask: `radial-gradient(farthest-side, transparent calc(100% - 3px), black calc(100% - 3px))`,
                      WebkitMask: `radial-gradient(farthest-side, transparent calc(100% - 3px), black calc(100% - 3px))`,
                    }}
                  />
                  <Image
                    src={logo}
                    alt="Hydrologiq Logo"
                    width="35px"
                    height="35px"
                    objectFit={"contain"}
                    position="absolute"
                    top="50%"
                    left="50%"
                    transform="translate(-50%, -50%)"
                    zIndex="1"
                  />
                </Box>
                <Text>Logiq AI is working out optimal schedules</Text>
              </VStack>
            </DialogBody>
          </DialogContent>
        </DialogPositioner>
      </DialogRoot>

      {/* Activity Create Dialog for Suggested Fulfilments */}
      {activityPreFillData && (
        <ActivityManage
          mode="create"
          order={activityPreFillData.order as any}
          availableAssets={activityPreFillData.availableAssets}
          defaultStartDate={activityPreFillData.startDate}
          defaultEndDate={activityPreFillData.endDate}
          defaultQuantity={activityPreFillData.quantity}
          defaultSite={activityPreFillData.siteId}
          activityKinds={[gqlTypes.ActivityKind.Fulfilment]}
          isOpen={createActivityDialogOpen}
          onClose={() => {
            setCreateActivityDialogOpen(false)
            setActivityPreFillData(null)
          }}
          onActivityCreated={() => {
            onActivityCreated?.()
            onRefresh?.()
            refetchSuggestedFulfilments()
          }}
        />
      )}
    </VStack>
  )
}

export const AssetAvailabilityCalendarSimple = AssetAvailabilityCalendarSimpleInner
export default AssetAvailabilityCalendarSimpleInner
